
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/types/event";
import { makeEventDetailsQueryKey } from "@/lib/reactQuery";
import { displayName } from "@/lib/utils";
import { capture as posthogCapture } from "@/services/posthog";
import { trackMetaEvent, metaEventParams } from "@/services/analytics";
import { useEventGroupAvailability } from "@/hooks/useEventGroupAvailability";

export const useEventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const groupAvailability = useEventGroupAvailability(id ? [id] : []);
  
  const { data: event, isLoading } = useQuery({
    queryKey: makeEventDetailsQueryKey(id),
    queryFn: async () => {
      if (!id) throw new Error("Event ID is required");
      const { data: eventData, error: eventError } = await supabase
        .from("events")
        .select(
          `
          *,
          event_tags(tag),
          event_agenda(time_slot, description),
          profiles!events_host_id_fkey(first_name, last_name, bio, avatar_url, member_type, joined_at, created_at),
          secondary_profiles:profiles!events_secondary_host_id_fkey(first_name, last_name, bio, avatar_url, member_type, joined_at, created_at)
        `
        )
        .eq("id", id)
        .maybeSingle();

      if (eventError) throw eventError;
      if (!eventData) return null;

      // Signed-out visitors can't read `profiles` (RLS), so the joins above
      // come back null. Fall back to the public host view for name + photo.
      const missingHostIds = [
        !eventData.profiles && eventData.host_id,
        !eventData.secondary_profiles && eventData.secondary_host_id,
      ].filter(Boolean) as string[];
      if (missingHostIds.length > 0) {
        const { data: publicHosts } = await supabase
          .from("public_host_profiles" as any)
          .select("id, first_name, last_name, avatar_url")
          .in("id", missingHostIds);
        const byId = new Map(((publicHosts ?? []) as any[]).map((h) => [h.id, h]));
        if (!eventData.profiles && eventData.host_id && byId.get(eventData.host_id)) {
          (eventData as any).profiles = byId.get(eventData.host_id);
        }
        if (!eventData.secondary_profiles && eventData.secondary_host_id && byId.get(eventData.secondary_host_id)) {
          (eventData as any).secondary_profiles = byId.get(eventData.secondary_host_id);
        }
      }

      // Fetch tickets to get attendees and calculate booked seats
      const { data: ticketsData, error: ticketsError } = await supabase
        .from("tickets")
        .select(
          `
          quantity,
          profiles!tickets_user_id_fkey (
            id,
            first_name,
            last_name
          )
        `
        )
        .eq("event_id", id)
        .eq("status", "confirmed");

      if (ticketsError) throw ticketsError;

      const { booked, attendees } = ticketsData.reduce(
        (sum, ticket) => {
          return {
            attendees: [
              ...sum.attendees,
              { ...ticket.profiles, quantity: ticket.quantity },
            ],
            booked: sum.booked + ticket.quantity,
          };
        },
        { booked: 0, attendees: [] as Event["attendees"] }
      );

      // Format agenda items, ensuring no duplication
      const agenda = Array.from(
        new Set(
          eventData.event_agenda?.map((a: any) => {
            const timePrefix = a.time_slot ? `${a.time_slot} - ` : "";
            return `${timePrefix}${a.description}`;
          }) || []
        )
      );

      const hostName =
        eventData.host ||
        displayName(
          eventData.profiles?.first_name,
          eventData.profiles?.last_name,
          'Nimi'
        );
        
      // Log the profiles data to debug
      console.log("Host profile data:", eventData.profiles);
      
      // Use joined_at if available, otherwise fall back to created_at
      const joinedDate = eventData.profiles?.joined_at || eventData.profiles?.created_at || null;
      console.log("Member joined/created date:", joinedDate);
        
      return {
        ...eventData,
        tags: eventData.event_tags?.map((t: any) => t.tag) || [],
        agenda,
        booked,
        attendees,
        host: hostName,
        allowed_booking_methods: eventData.allowed_booking_methods || ['stripe', 'credits', 'unlimited'],
        required_tags: eventData.required_tags || [],
        profiles: eventData.profiles ? {
          ...eventData.profiles,
          joined_at: joinedDate
        } : undefined
      } as Event;
    },
    enabled: !!id,
  });

  // Fire event_viewed once per event id when the event finishes loading.
  const firedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (event && event.id && firedForRef.current !== event.id) {
      firedForRef.current = event.id;
      posthogCapture("event_viewed", {
        event_id: event.id,
        title: event.title,
        host_id: (event as any).host_id ?? undefined,
        price: (event as any).price ?? undefined,
        is_secret_salon: Array.isArray(event.tags) && event.tags.includes("secret-salon"),
      });
      trackMetaEvent("ViewContent", metaEventParams(event as any));
    }
  }, [event]);

  return {
    event: event ? { ...event, groups: groupAvailability.byEvent.get(event.id) ?? [] } : event,
    isLoading: isLoading || groupAvailability.isLoading,
    isGroupAvailabilityError: groupAvailability.isError,
    refetchGroupAvailability: groupAvailability.refetch,
  };
};
